import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Link2, PenLine, CheckCircle2, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getSession, signSession, linkRelease, getReleases, sessionPdfUrl, getSplitInvitees, DOC_TYPES } from "@/lib/afmApi";
import { fmtDate } from "@/lib/format";
import { Loading, ErrorState, SectionTitle } from "@/components/common";
import { StatusBadge, SESSION_STATUS } from "@/components/StatusBadge";
import { SignaturePad } from "@/components/SignaturePad";

export default function SplitSheetDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [invitees, setInvitees] = useState([]);
  const [status, setStatus] = useState("loading");
  const [sig, setSig] = useState(null);
  const [biometric, setBiometric] = useState(false);
  const [signing, setSigning] = useState(false);
  const [drawNew, setDrawNew] = useState(false);
  const [releases, setReleases] = useState([]);
  const [releaseId, setReleaseId] = useState("");
  const [linking, setLinking] = useState(false);

  const load = () => {
    getSession(id, user.id)
      .then((d) => { setSession(d); setStatus("ok"); })
      .catch(() => setStatus("error"));
    getSplitInvitees(id).then(setInvitees).catch(() => {});
  };

  useEffect(() => {
    setStatus("loading");
    load();
    getReleases(user.id).then(setReleases).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user.id]);

  const sign = async () => {
    if (!sig) { toast.error("Dibuja tu firma primero"); return; }
    setSigning(true);
    try {
      await signSession(id, { user_id: user.id, signature_data_url: sig, biometric_verified: biometric });
      toast.success("Firma registrada");
      setSig(null); setBiometric(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo firmar");
    } finally { setSigning(false); }
  };

  const doLink = async () => {
    if (!releaseId) { toast.error("Selecciona un lanzamiento"); return; }
    setLinking(true);
    try {
      await linkRelease(id, { release_id: releaseId, user_id: user.id });
      toast.success("Lanzamiento vinculado · reparto de regalías activado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo vincular");
    } finally { setLinking(false); }
  };

  if (status === "loading") return <Loading />;
  if (status === "error" || !session) return <ErrorState onRetry={load} />;

  const sigRaw = session?.signatures;
  const signatures = Array.isArray(sigRaw) ? sigRaw : (sigRaw && typeof sigRaw === "object" ? Object.values(sigRaw) : []);
  const signedIds = Array.isArray(sigRaw)
    ? new Set(sigRaw.map((s) => s.user_id))
    : new Set(sigRaw && typeof sigRaw === "object" ? Object.keys(sigRaw) : []);
  const alreadySigned = signedIds.has(user.id);
  const sealed = session?.status === "sealed";
  const participants = Array.isArray(session?.participants) ? session.participants : Object.values(session?.participants || {});

  return (
    <div className="space-y-8" data-testid="split-sheet-detail-page">
      <button onClick={() => navigate("/split-sheets")} data-testid="back-to-split-sheets" className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-black">
        <ArrowLeft className="h-4 w-4" /> Volver a Split Sheets
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <StatusBadge map={SESSION_STATUS} status={session.status} />
            <span className="rounded-full bg-[#9E1B22]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#9E1B22]" data-testid="session-doctype">{DOC_TYPES[session.doc_type] || "Acuerdo"}</span>
            <span className="text-xs text-slate-400">{(session.project_type || "").toUpperCase()} · {fmtDate(session.session_date)}</span>
          </div>
          <h1 className="mt-2 font-serif text-4xl font-extrabold tracking-tight text-black">{session.project_title}</h1>
          {session.location && <div className="mt-1 text-sm text-slate-500">{session.location}</div>}
        </div>
        {sealed && (
          <a href={sessionPdfUrl(id, user.id)} target="_blank" rel="noopener noreferrer" data-testid="download-pdf-button" className="flex items-center gap-2 rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">
            <Download className="h-4 w-4" /> Descargar PDF sellado
          </a>
        )}
      </div>

      {/* Participants */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="participants-table">
        <div className="border-b border-slate-200 px-5 py-4"><SectionTitle>Participantes & reparto</SectionTitle></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3 font-semibold">Participante</th>
                <th className="px-5 py-3 font-semibold">Rol</th>
                <th className="px-5 py-3 text-right font-semibold">Master %</th>
                <th className="px-5 py-3 text-right font-semibold">Publishing %</th>
                <th className="px-5 py-3 text-center font-semibold">Firma</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((p) => (
                <tr key={p.user_id} className="border-t border-slate-100">
                  <td className="px-5 py-3"><div className="font-medium text-slate-800">{p.name}</div><div className="text-xs text-slate-400">{p.email}</div></td>
                  <td className="px-5 py-3 capitalize text-slate-600">{p.role}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-700">{p.master_pct}%</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-700">{p.publishing_pct}%</td>
                  <td className="px-5 py-3 text-center">
                    {signedIds.has(p.user_id) ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Firmado</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Clock className="h-4 w-4" /> Pendiente</span>
                    )}
                  </td>
                </tr>
              ))}
              {invitees.map((iv) => (
                <tr key={`inv-${iv.email}`} data-testid={`invitee-row-${iv.email}`} className="border-t border-slate-100 bg-amber-50/40">
                  <td className="px-5 py-3"><div className="flex items-center gap-2"><span className="font-medium text-slate-800">{iv.name || iv.email}</span><span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700">Invitado</span></div><div className="text-xs text-slate-400">{iv.email}</div></td>
                  <td className="px-5 py-3 capitalize text-slate-600">{iv.role}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-700">{iv.master_pct}%</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-700">{iv.publishing_pct}%</td>
                  <td className="px-5 py-3 text-center">
                    {iv.status === "signed"
                      ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Firmado</span>
                      : <span className="inline-flex items-center gap-1 text-xs text-amber-600"><Clock className="h-4 w-4" /> Invitado</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sign */}
      {!sealed && !alreadySigned && (
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="signature-section">
          <SectionTitle><span className="flex items-center gap-2"><PenLine className="h-5 w-5" /> Tu firma</span></SectionTitle>
          {user.saved_signature_url && !drawNew ? (
            <div data-testid="saved-signature-block">
              <div className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-3">
                <img src={user.saved_signature_url} alt="Firma guardada" data-testid="saved-signature-preview" className="mx-auto h-28 w-auto max-w-full object-contain" />
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button type="button" onClick={() => setSig(user.saved_signature_url)} data-testid="use-saved-signature" className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${sig === user.saved_signature_url ? "bg-emerald-600 text-white" : "border border-slate-300 hover:border-black"}`}>
                  {sig === user.saved_signature_url ? "Firma guardada seleccionada ✓" : "Usar mi firma guardada"}
                </button>
                <button type="button" onClick={() => { setDrawNew(true); setSig(null); }} data-testid="draw-new-signature" className="text-sm font-medium text-slate-500 hover:text-black">Dibujar otra firma</button>
              </div>
            </div>
          ) : (
            <>
              <SignaturePad onChange={setSig} />
              {user.saved_signature_url && (
                <button type="button" onClick={() => { setDrawNew(false); setSig(null); }} data-testid="back-to-saved-signature" className="mt-2 text-sm font-medium text-slate-500 hover:text-black">Usar mi firma guardada</button>
              )}
            </>
          )}
          <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={biometric} onChange={(e) => setBiometric(e.target.checked)} data-testid="biometric-checkbox" className="h-4 w-4 rounded border-slate-300" />
            Verificación biométrica confirmada
          </label>
          <button onClick={sign} disabled={signing || !sig} data-testid="sign-button" className="mt-4 rounded-lg bg-black px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
            {signing ? "Firmando…" : "Firmar acuerdo"}
          </button>
        </div>
      )}
      {alreadySigned && !sealed && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-700" data-testid="already-signed">
          Ya firmaste este acuerdo. Esperando a los demás participantes.
        </div>
      )}

      {/* Link release */}
      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="link-release-section">
        <SectionTitle><span className="flex items-center gap-2"><Link2 className="h-5 w-5" /> Vincular a un lanzamiento</span></SectionTitle>
        <p className="mb-3 text-sm text-slate-500">Al vincular, el reparto definido aquí se aplica automáticamente a las regalías del lanzamiento.</p>
        <div className="flex flex-wrap gap-3">
          <select value={releaseId} onChange={(e) => setReleaseId(e.target.value)} data-testid="link-release-select" className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-black">
            <option value="">Selecciona un lanzamiento…</option>
            {releases.map((r) => (<option key={r.id} value={r.id}>{r.title}</option>))}
          </select>
          <button onClick={doLink} disabled={linking || !releaseId} data-testid="link-release-button" className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-semibold hover:border-black disabled:opacity-50">
            {linking ? "Vinculando…" : "Vincular"}
          </button>
        </div>
      </div>
    </div>
  );
}
